import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { api, installApiErrorHandlers } from "@/lib/api";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
    // Phase 4-b: identity comes from a cookie-backed user, not a UUID.
    // `auth`: null = still checking, false = unauthenticated, object = user.
    const [auth, setAuth] = useState(null);
    const [profile, setProfile] = useState(null);
    const [vault, setVault] = useState(null);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerIntent, setDrawerIntent] = useState(null);
    const [subscriptionDrawerOpen, setSubscriptionDrawerOpen] = useState(false);
    const [subscriptionReason, setSubscriptionReason] = useState(null);
    const skipMeOnMount = useRef(
        typeof window !== "undefined" &&
            window.location.hash?.includes("session_id=")
    );

    const refreshUser = useCallback(async () => {
        try {
            const { data } = await api.get("/me");
            setAuth(data);
            return data;
        } catch (err) {
            if (err?.response?.status === 401) {
                setAuth(false);
            }
            return null;
        }
    }, []);

    const refreshAll = useCallback(async () => {
        try {
            const [p, v] = await Promise.all([
                api.get("/profile"),
                api.get("/vault"),
            ]);
            setProfile(p.data);
            setVault(v.data);
        } catch (err) {
            if (err?.response?.status === 401) return; // not logged in yet
            console.error("[session] failed to load profile/vault", err);
        }
    }, []);

    useEffect(() => {
        // Per playbook: skip /me check if we're in the OAuth callback path.
        // AuthCallback will exchange session_id and then call us back.
        if (skipMeOnMount.current) {
            setLoading(false);
            return;
        }
        (async () => {
            const user = await refreshUser();
            if (user) await refreshAll();
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Centralized API error routing. The interceptor lives here so it has
    // closure access to fresh setters. We re-install whenever a handler ref
    // changes (rare). The handlers fan out to:
    //   401 → drop to anonymous (router will render LandingPage)
    //   402 trial_expired → open SubscriptionDrawer + bump user state
    //   403 tier_required → open SubscriptionDrawer at the required tier
    // Per-component 402 insufficient_balance handling is intentionally NOT
    // routed here — components show contextual UX (NexaVault topup card).
    useEffect(() => {
        const eject = installApiErrorHandlers({
            onUnauthenticated: () => {
                setAuth(false);
                setProfile(null);
                setVault(null);
            },
            onTrialExpired: (detail) => {
                setSubscriptionReason({
                    code: "trial_expired",
                    required: detail?.required_tier || "creator",
                });
                setSubscriptionDrawerOpen(true);
                // Refresh user so `trial_active` flips in state.
                refreshUser().catch(() => {});
            },
            onTierRequired: (detail) => {
                setSubscriptionReason({
                    code: "tier_required",
                    required: detail?.required_tier || "creator",
                    feature: detail?.feature || null,
                });
                setSubscriptionDrawerOpen(true);
            },
        });
        return eject;
    }, [refreshUser]);

    // Helper that the AuthCallback page calls after a successful exchange.
    const hydrateAfterLogin = useCallback(async () => {
        setLoading(true);
        const user = await refreshUser();
        if (user) await refreshAll();
        setLoading(false);
        return user;
    }, [refreshUser, refreshAll]);

    const setBalanceFast = useCallback((newBalance) => {
        setVault((v) => (v ? { ...v, balance: newBalance } : v));
        setAuth((u) => (u ? { ...u, balance: newBalance } : u));
    }, []);

    const refreshVault = useCallback(async () => {
        try {
            const { data } = await api.get("/vault");
            setVault(data);
        } catch (err) {
            /* swallow */
        }
    }, []);

    const refreshProfile = useCallback(async () => {
        try {
            const { data } = await api.get("/profile");
            setProfile(data);
        } catch (err) {
            /* swallow */
        }
    }, []);

    const saveProfile = useCallback(async (patch) => {
        const { data } = await api.put("/profile", patch);
        setProfile(data);
        return data;
    }, []);

    const topUp = useCallback(async () => {
        try {
            const { data } = await api.post("/v1/ledger/topup", { amount_usd: 5 });
            setVault((v) => {
                if (!v) return v;
                const existing = v.transactions || [];
                const alreadyHas = existing.some(
                    (t) => t.id === data.transaction?.id
                );
                const nextTxs = alreadyHas
                    ? existing
                    : data.transaction
                      ? [data.transaction, ...existing]
                      : existing;
                return { ...v, balance: data.balance, transactions: nextTxs };
            });
            setAuth((u) => (u ? { ...u, balance: data.balance } : u));
            toast.success("Topped up", {
                description: `+${data.transaction?.amount ?? 500} NXT added to your vault.`,
            });
            return data;
        } catch (err) {
            // 402 trial_expired and 403 tier_required are handled centrally
            // by the axios interceptor in `installApiErrorHandlers`.
            throw err;
        }
    }, []);

    const openDrawer = useCallback((intent = null) => {
        setDrawerIntent(intent);
        setDrawerOpen(true);
    }, []);

    const openSubscriptionDrawer = useCallback((reason = null) => {
        setSubscriptionReason(reason);
        setSubscriptionDrawerOpen(true);
    }, []);

    const subscribe = useCallback(async (tier) => {
        const { data } = await api.post("/subscriptions/subscribe", { tier });
        setAuth(data);
        await refreshVault();
        toast.success(`Welcome to ${tier === "pro" ? "Pro" : "Creator"}`, {
            description: `Plan active · balance ${data.balance} NXT`,
        });
        return data;
    }, [refreshVault]);

    const signOut = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        setAuth(false);
        setProfile(null);
        setVault(null);
        toast.success("Signed out");
    }, []);

    const value = useMemo(
        () => ({
            // auth state
            auth, // null = checking, false = anonymous, object = user
            user: auth || null,
            isAuthenticated: !!auth,
            tier: auth?.tier || null,
            trialActive: auth?.trial_active ?? null,
            trialExpiresAt: auth?.trial_expires_at || null,
            hydrateAfterLogin,
            refreshUser,
            signOut,
            subscribe,

            // legacy / app state
            profile,
            vault,
            balance: vault?.balance ?? auth?.balance ?? 0,
            loading,

            // NexaVault drawer
            drawerOpen,
            drawerIntent,
            setDrawerOpen,
            setDrawerIntent,
            openDrawer,

            // Subscription drawer
            subscriptionDrawerOpen,
            subscriptionReason,
            setSubscriptionDrawerOpen,
            openSubscriptionDrawer,

            // mutations
            refreshAll,
            refreshVault,
            refreshProfile,
            setBalanceFast,
            saveProfile,
            topUp,
        }),
        [
            auth,
            profile,
            vault,
            loading,
            drawerOpen,
            drawerIntent,
            subscriptionDrawerOpen,
            subscriptionReason,
            hydrateAfterLogin,
            refreshUser,
            signOut,
            subscribe,
            openDrawer,
            openSubscriptionDrawer,
            refreshAll,
            refreshVault,
            refreshProfile,
            setBalanceFast,
            saveProfile,
            topUp,
        ]
    );

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession() {
    const ctx = useContext(SessionContext);
    if (!ctx) {
        throw new Error("useSession must be used inside <SessionProvider>");
    }
    return ctx;
}
